import { useState, useEffect, useRef } from 'react';
import { FieldValue } from '../../types/QueryBuilder';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';

const DEBOUNCE_MS = 300;

export const useCriteriaValueSearch = (criteria: string, input: string, limit = 20) => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const [options, setOptions] = useState<FieldValue[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const requestId = useRef(0);

    useEffect(() => {
        const q = input.trim();
        if (!isReady || !q) {
            setOptions([]);
            setLoading(false);
            return;
        }

        const id = ++requestId.current;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const result = await authFetch(
                    `/getDataQueryCriteriaValues/${encodeURIComponent(criteria)}?q=${encodeURIComponent(q)}&limit=${limit}`,
                    { method: 'GET' }
                );
                if (id !== requestId.current) return;
                setOptions(Array.isArray(result) ? (result as FieldValue[]) : []);
                setError(false);
            } catch {
                if (id !== requestId.current) return;
                setOptions([]);
                setError(true);
            } finally {
                if (id === requestId.current) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [authFetch, isReady, criteria, input, limit]);

    return { options, loading, error };
};
